/**
 * Export CSV Button Component
 *
 * @description Reusable button for CSV export with GA4 tracking
 * @version 2.0.0
 * @updated 2025-10-28 - Refactored to use useCSVExport hook
 * @created 2025-10-27
 */

import { Button } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { useCSVExport } from '../../../../hooks/useCSVExport';

/**
 * Calculation Data Input
 */
interface CalculationData {
  year: number;
  monthlyConsumption: number[];
  hourlyConfig: {
    weekday: number[];
    weekend: number[];
  };
}

/**
 * Component Props
 */
interface ExportCSVButtonProps {
  /** Calculation data for CSV generation */
  calculationData: CalculationData;

  /** Button variant */
  variant?: 'filled' | 'outline' | 'light';

  /** Button size */
  size?: 'xs' | 'sm' | 'md' | 'lg';

  /** Full width button */
  fullWidth?: boolean;

  /** Compact mode (no text, icon only) */
  compact?: boolean;

  /** Location for GA4 tracking */
  location?: 'calculator' | 'dashboard_quick_actions' | 'dashboard_card';
}

/**
 * Export CSV Button Component
 *
 * Reusable button for exporting calculation data to CSV.
 * Handles loading states, error handling, and success notifications.
 */
export function ExportCSVButton({
  calculationData,
  variant = 'filled',
  size = 'md',
  fullWidth = false,
  compact = false,
  location = 'calculator',
}: ExportCSVButtonProps) {
  // Use custom hook for CSV export logic
  const { loading, handleExport } = useCSVExport({
    calculationData,
    location,
  });

  return (
    <Button
      variant={variant}
      color={variant === 'filled' ? 'green' : 'gray'}
      size={size}
      leftSection={!compact ? <IconDownload size={20} /> : undefined}
      onClick={handleExport}
      loading={loading}
      disabled={loading}
      fullWidth={fullWidth}
      aria-label="Pobierz dane CSV"
    >
      {compact ? <IconDownload size={20} /> : 'Pobierz dane CSV'}
    </Button>
  );
}
